import { Component, EventEmitter, Input, Output, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { ThemeService } from '../../../core/services/theme.service';
import { Subject, takeUntil } from 'rxjs';

type SidebarNavItem = {
    name: string;
    href?: string;
    icon?: string;
    children?: SidebarNavItem[];
    collapsible?: boolean;
};

@Component({
    selector: 'app-sidebar',
    standalone: true,
    imports: [CommonModule, RouterLink, LucideIconsModule],
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnDestroy {
    defaultNavigation: SidebarNavItem[] = [
        { name: "Portfolio", href: "/developer/dashboard", icon: "layout-dashboard" },
        { name: "Projects", href: "/developer/projects", icon: "building-2" },
        { name: "Properties", href: "/developer/properties", icon: "home" }
    ];

    @Input() isMobileOpen = false;
    @Input() navigation: SidebarNavItem[] = this.defaultNavigation;
    @Output() mobileClose = new EventEmitter<void>();
    readonly themeService = inject(ThemeService);
    /** Names of collapsible nav groups currently expanded (e.g. the "More" dropdown). */
    expandedGroups = new Set<string>();

    toggleGroup(name: string): void {
        const key = String(name || '').trim();
        if (!key) return;
        if (this.expandedGroups.has(key)) {
            this.expandedGroups.delete(key);
        } else {
            this.expandedGroups.add(key);
        }
    }

    isGroupExpanded(name: string): boolean {
        return this.expandedGroups.has(String(name || '').trim());
    }

    isCollapsible(item: SidebarNavItem): boolean {
        return Boolean(item?.collapsible) && Array.isArray(item?.children) && item.children.length > 0;
    }
    private readonly localLogoBlack = 'https://assetsnibni.zohostratus.com/images/logos/nibni-logo-black.avif';
    private readonly localLogoWhite = 'https://assetsnibni.zohostratus.com/images/logos/nibni-logo-white.avif';

    get logoSrc(): string {
        const isDarkTheme = this.themeService.theme() !== 'white';
        return isDarkTheme ? this.localLogoWhite : this.localLogoBlack;
    }

    private readonly destroy$ = new Subject<void>();
    private readonly router = inject(Router);
    private currentUrl = this.router.url || '';

    constructor() {
        this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
            if (event instanceof NavigationEnd) this.currentUrl = event.urlAfterRedirects || event.url || '';
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Which nav href is highlighted right now, computed from the router URL.
     *
     * RouterLinkActive could not express the overlap in the nav: one href may
     * be a URL prefix of ANOTHER nav item (/developer/offers vs
     * /developer/offers/approvals), so a plain prefix match lit BOTH chips on
     * the approvals page (client report 2026-09-16), while exact matching went
     * the other way — the Offers chip went dark on the offers page's own tabs
     * (/developer/offers/unit). Rule instead: the item whose href is the
     * LONGEST segment-boundary prefix of the current URL wins; everything
     * else stays dark. Parents (Properties) still light up on their own
     * child pages (…/properties/create) when no sibling nav item claims it.
     */
    private matchesPath(url: string, path: string): boolean {
        return url === path || url.startsWith(path + '/');
    }

    private allNavHrefs(): string[] {
        const out: string[] = [];
        for (const item of this.navigation || []) {
            const h = String(item?.href || '').trim();
            if (h) out.push(h);
            for (const child of item?.children || []) {
                const ch = String(child?.href || '').trim();
                if (ch) out.push(ch);
            }
        }
        return out;
    }

    isNavActive(href?: string): boolean {
        const path = String(href || '').trim();
        if (!path) return false;
        const url = String(this.currentUrl || '').split('?')[0].split('#')[0];
        if (!url || !this.matchesPath(url, path)) return false;
        return !this.allNavHrefs().some(
            (other) => other !== path && other.length > path.length && this.matchesPath(url, other)
        );
    }

    /** Active chip classes, or '' when the href is not the current page. */
    navActiveClasses(href?: string): string {
        return this.isNavActive(href)
            ? 'bg-[color:var(--secondary)] text-[color:var(--primary)] border-[color:var(--ring)] shadow-sm'
            : '';
    }

    closeMobile(): void {
        this.mobileClose.emit();
    }
}
